import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { prefersReducedMotion } from '@/components/layout/shell/entrance';
import { liftOffset } from './fanGeometry';
import type { Bezier } from './schedule';

/*
  useFanMotion — один rAF-движок веера сервисов (spec-4 §7.1). Пишет атрибуты прямо в узлы svg, без
  состояния React (прецедент — Наблюдатель, R36): поворот и отрыв от заклёпки пластины, её копии у шва,
  её контура в обрезке шва и рамки фокуса — одним transform, поэтому они не расходятся; радиус круга
  света; прозрачность.

  Твин на каждое значение id × { angle, opacity, light, lift }: от текущего к цели, с длительностью,
  задержкой и кривой. Новая цель подхватывает с текущего значения — на ходу, без рывка. Когда всё на
  месте, цикла нет. При скрытой вкладке и уменьшенном движении (R19) — сразу в цель.

  Узлы ищутся по data-атрибутам (data-plate, data-seam-copy, data-seam-clip, data-light, data-focus =
  id пластины); кэш пересобирается в layout-эффекте после каждого рендера и сразу получает текущие
  значения — React эти атрибуты не рисует и не затирает.
*/

export interface FanValues {
  angle: number;
  opacity: number;
  light: number;
  /** Отрыв от заклёпки вдоль оси пластины, в единицах (0 — у заклёпки, FAN_LIFT — активная). */
  lift: number;
}
type Channel = keyof FanValues;

export interface FanTween {
  duration: number;
  delay?: number;
  ease: Bezier | string;
}

export interface FanMotion {
  /** К цели от текущего значения. */
  to(id: string, values: Partial<FanValues>, tween: FanTween): void;
  /** Сразу в значение, без движения; идущий твин этих значений снимается. */
  jump(id: string, values: Partial<FanValues>): void;
  /** Текущее значение пластины (или undefined, если её ещё не ставили). */
  value(id: string): Readonly<FanValues> | undefined;
  /** Всё на месте. */
  settled(): Promise<void>;
}

interface Track {
  from: number;
  to: number;
  start: number;
  duration: number;
  ease: (t: number) => number;
}

interface Nodes {
  /** Поворот и отрыв: пластина, копия у шва, контур в обрезке шва, рамка фокуса. */
  turn: Element[];
  /** Прозрачность: пластина, копия у шва. */
  fade: Element[];
  /** Круг света. */
  light: Element[];
}

type Attr = 'transform' | 'opacity' | 'r';

const DEFAULTS: FanValues = { angle: 0, opacity: 1, light: 500, lift: 0 };
const SELECTOR = '[data-plate],[data-seam-copy],[data-seam-clip],[data-light],[data-focus]';

/* ---------- Кривые: cubic-bezier числами или строкой «cubic-bezier(…)» ---------- */

const curves = new Map<string, (t: number) => number>();

function solveBezier([x1, y1, x2, y2]: Bezier): (t: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sx = (u: number) => ((ax * u + bx) * u + cx) * u;
  const sy = (u: number) => ((ay * u + by) * u + cy) * u;
  const dx = (u: number) => (3 * ax * u + 2 * bx) * u + cx;
  return (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    // Ньютон, при плохой производной — деление пополам.
    let u = t;
    for (let i = 0; i < 6; i++) {
      const e = sx(u) - t;
      const d = dx(u);
      if (Math.abs(e) < 1e-5) return sy(u);
      if (Math.abs(d) < 1e-6) break;
      u -= e / d;
    }
    let lo = 0;
    let hi = 1;
    u = t;
    for (let i = 0; i < 24; i++) {
      const e = sx(u) - t;
      if (Math.abs(e) < 1e-5) break;
      if (e > 0) hi = u;
      else lo = u;
      u = (lo + hi) / 2;
    }
    return sy(u);
  };
}

function curve(ease: Bezier | string): (t: number) => number {
  const key = typeof ease === 'string' ? ease : ease.join(',');
  const hit = curves.get(key);
  if (hit) return hit;
  let pts: Bezier = [0.4, 0, 0.2, 1];
  if (typeof ease !== 'string') pts = ease;
  else if (ease === 'linear') pts = [0, 0, 1, 1];
  else if (ease === 'ease-out') pts = [0, 0, 0.58, 1];
  else if (ease === 'ease-in') pts = [0.42, 0, 1, 1];
  else {
    const m = ease.match(/cubic-bezier\(([^)]+)\)/);
    const v = m?.[1].split(',').map(Number);
    if (v?.length === 4 && v.every(Number.isFinite)) pts = [v[0], v[1], v[2], v[3]];
  }
  const f = solveBezier(pts);
  curves.set(key, f);
  return f;
}

/* ---------- Движок ---------- */

export function useFanMotion(root: RefObject<SVGSVGElement | null>): FanMotion {
  const [e] = useState(() => new Engine());

  // После каждого рендера: узлы заново и сразу текущие значения (новые пластины, сменившаяся рамка фокуса).
  useLayoutEffect(() => {
    e.rebuild(root.current);
  });
  // Цикл живёт, пока смонтирован; в строгом режиме разработки перезапускается после повторного монтажа.
  useEffect(() => {
    e.kick();
    return () => e.stop();
  }, [e]);

  return e.api;
}

class Engine {
  private values = new Map<string, FanValues>();
  private tracks = new Map<string, Partial<Record<Channel, Track>>>();
  private nodes = new Map<string, Nodes>();
  private raf = 0;
  private waiters: (() => void)[] = [];
  /** Что уже записано в узлы: то же значение не пишется второй раз (лишний пересчёт стилей). */
  private written: Record<Attr, WeakMap<Element, string>> = {
    transform: new WeakMap(),
    opacity: new WeakMap(),
    r: new WeakMap(),
  };

  readonly api: FanMotion = {
    to: (id, values, tween) => this.to(id, values, tween),
    jump: (id, values) => this.jump(id, values),
    value: (id) => this.values.get(id),
    settled: () =>
      this.tracks.size === 0 ? Promise.resolve() : new Promise<void>((r) => this.waiters.push(r)),
  };

  rebuild(svg: SVGSVGElement | null) {
    this.nodes.clear();
    if (!svg) return;
    for (const el of svg.querySelectorAll(SELECTOR)) {
      const d = (el as SVGElement).dataset;
      const id = d.plate ?? d.seamCopy ?? d.seamClip ?? d.light ?? d.focus;
      if (id === undefined) continue;
      let n = this.nodes.get(id);
      if (!n) {
        n = { turn: [], fade: [], light: [] };
        this.nodes.set(id, n);
      }
      if (d.light !== undefined) n.light.push(el);
      else n.turn.push(el);
      if (d.plate !== undefined || d.seamCopy !== undefined) n.fade.push(el);
    }
    // Ушедшие пластины забываются: вернётся — встанет заново.
    for (const id of [...this.values.keys()]) {
      if (!this.nodes.has(id)) {
        this.values.delete(id);
        this.tracks.delete(id);
      }
    }
    for (const id of this.nodes.keys()) this.write(id);
  }

  kick() {
    if (this.tracks.size > 0 && !this.raf) this.raf = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private current(id: string): FanValues {
    let v = this.values.get(id);
    if (!v) {
      v = { ...DEFAULTS };
      this.values.set(id, v);
    }
    return v;
  }

  private jump(id: string, values: Partial<FanValues>) {
    const v = this.current(id);
    const t = this.tracks.get(id);
    for (const c of Object.keys(values) as Channel[]) {
      const x = values[c];
      if (x === undefined) continue;
      v[c] = x;
      if (t) delete t[c];
    }
    if (t && Object.keys(t).length === 0) this.tracks.delete(id);
    this.write(id);
    this.settle();
  }

  private to(id: string, values: Partial<FanValues>, tween: FanTween) {
    const fresh = !this.values.has(id);
    if (fresh || prefersReducedMotion() || document.hidden || tween.duration <= 0) {
      this.jump(id, values);
      return;
    }
    const v = this.current(id);
    const start = performance.now() + (tween.delay ?? 0);
    const ease = curve(tween.ease);
    let t = this.tracks.get(id);
    for (const c of Object.keys(values) as Channel[]) {
      const x = values[c];
      if (x === undefined) continue;
      const running = t?.[c];
      // Та же цель уже в пути — не перезапускать (иначе каждый рендер начинал бы движение заново).
      if (running && running.to === x) continue;
      if (!running && v[c] === x) continue;
      t ??= {};
      t[c] = { from: v[c], to: x, start, duration: tween.duration, ease };
    }
    if (t && Object.keys(t).length > 0) this.tracks.set(id, t);
    this.kick();
  }

  private tick = (now: number) => {
    this.raf = 0;
    for (const [id, t] of this.tracks) {
      const v = this.current(id);
      for (const c of Object.keys(t) as Channel[]) {
        const tr = t[c];
        if (!tr) continue;
        const p = (now - tr.start) / tr.duration;
        if (p <= 0) continue;
        if (p >= 1) {
          v[c] = tr.to;
          delete t[c];
        } else v[c] = tr.from + (tr.to - tr.from) * tr.ease(p);
      }
      if (Object.keys(t).length === 0) this.tracks.delete(id);
      this.write(id);
    }
    if (this.tracks.size > 0) this.raf = requestAnimationFrame(this.tick);
    else this.settle();
  };

  private settle() {
    if (this.tracks.size > 0) return;
    const w = this.waiters;
    this.waiters = [];
    for (const r of w) r();
  }

  private write(id: string) {
    const n = this.nodes.get(id);
    const v = this.values.get(id);
    if (!n || !v) return;
    const a = Math.round(v.angle * 100) / 100;
    // Сдвиг — после поворота: пластина отходит вдоль своей оси в текущем угле.
    const d = v.lift > 0.01 ? liftOffset(v.angle, v.lift) : null;
    const turn = d
      ? `translate(${Math.round(d.x * 100) / 100} ${Math.round(d.y * 100) / 100}) rotate(${a})`
      : `rotate(${a})`;
    for (const el of n.turn) this.set(el, 'transform', turn);
    const o = String(Math.round(v.opacity * 1000) / 1000);
    for (const el of n.fade) this.set(el, 'opacity', o);
    const r = String(Math.round(v.light * 10) / 10);
    for (const el of n.light) this.set(el, 'r', r);
  }

  private set(el: Element, name: Attr, value: string) {
    if (this.written[name].get(el) === value) return;
    this.written[name].set(el, value);
    el.setAttribute(name, value);
  }
}
